// File: components/PropertyPanels/DTMFNodeProperties.jsx
import React from 'react';
import {
    Box,
    VStack,
    Text,
    Input,
    Select,
    Button,
    FormLabel,
    Switch,
    Divider,
    IconButton,
    SimpleGrid,
    Flex,
    Icon
} from '@chakra-ui/react';
import {
    FaPlus,
    FaTrash,
    FaSave,
    FaHashtag,
    FaClock,
} from 'react-icons/fa';

const DTMFNodeProperties = ({ nodeConfig, setNodeConfig, updateNodeData }) => {
    return (
        <VStack spacing={4} align="stretch">
            <Box>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    value={nodeConfig.label}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., IVR Menu Selection"
                    size="sm"
                />
            </Box>

            <Divider />

            <Box>
                <Flex justify="space-between" align="center" mb={3}>
                    <FormLabel fontSize="sm" mb={0}>Configure DTMF Responses</FormLabel>
                    <Button
                        size="xs"
                        fontSize={"xs"}
                        // leftIcon={<Icon as={FaPlus} />}
                        colorScheme="blue"
                        onClick={() => {
                            const newResponses = [...(nodeConfig.dtmfResponses || [])];
                            const newId = `dtmf_${Date.now()}`;
                            newResponses.push({
                                key: '',
                                label: '',
                                id: newId,
                                timeoutEnabled: false,
                                timeoutDuration: 30,
                                timeoutUnit: 'seconds'
                            });
                            setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                        }}
                    >
                        Add Response
                    </Button>
                </Flex>

                {(!nodeConfig.dtmfResponses || nodeConfig.dtmfResponses.length === 0) ? (
                    <Box
                        textAlign="center"
                        py={8}
                        border="2px dashed"
                        borderColor="gray.300"
                        borderRadius="lg"
                        bg="gray.50"
                    >
                        <Icon as={FaHashtag} w={8} h={8} color="gray.400" mb={2} />
                        <Text fontSize="sm" color="gray.600" mb={3}>
                            No DTMF responses configured
                        </Text>
                        <Button
                            size="sm"
                            leftIcon={<Icon as={FaPlus} />}
                            colorScheme="blue"
                            variant="outline"
                            onClick={() => {
                                const newId = `dtmf_${Date.now()}`;
                                setNodeConfig({
                                    ...nodeConfig,
                                    dtmfResponses: [{
                                        key: '',
                                        label: '',
                                        id: newId,
                                        timeoutEnabled: false,
                                        timeoutDuration: 30,
                                        timeoutUnit: 'seconds'
                                    }]
                                });
                            }}
                        >
                            Add Your First Response
                        </Button>
                    </Box>
                ) : (
                    <VStack spacing={3} align="stretch">
                        {(nodeConfig.dtmfResponses || []).map((response, index) => (
                            <Box
                                key={response.id || index}
                                p={3}
                                border="1px solid"
                                borderColor="orange.300"
                                borderRadius="lg"
                                bg="#fff7ed"
                            >
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontSize="xs" fontWeight="semibold">
                                        DTMF Response {index + 1}
                                    </Text>
                                    <IconButton
                                        icon={<Icon as={FaTrash} />}
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => {
                                            const newResponses = (nodeConfig.dtmfResponses || []).filter((_, i) => i !== index);
                                            setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                                        }}
                                        aria-label="Delete response"
                                    />
                                </Flex>

                                <VStack spacing={3} align="stretch">
                                    <SimpleGrid columns={2} spacing={2}>
                                        <Box>
                                            <FormLabel fontSize="xs">DTMF Key</FormLabel>
                                            <Input
                                                bg={"white"}
                                                value={response.key || ''}
                                                onChange={(e) => {
                                                    const newResponses = [...(nodeConfig.dtmfResponses || [])];
                                                    newResponses[index] = {
                                                        ...response,
                                                        key: e.target.value
                                                    };
                                                    setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                                                }}
                                                placeholder="e.g., 1 or *"
                                                size="sm"
                                                fontFamily="mono"
                                                maxLength={2}
                                            />
                                        </Box>
                                        <Box>
                                            <FormLabel fontSize="xs">Label</FormLabel>
                                            <Input
                                                value={response.label || ''}
                                                bg={"white"}
                                                onChange={(e) => {
                                                    const newResponses = [...(nodeConfig.dtmfResponses || [])];
                                                    newResponses[index] = {
                                                        ...response,
                                                        label: e.target.value
                                                    };
                                                    setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                                                }}
                                                placeholder="e.g., Support"
                                                size="sm"
                                            />
                                        </Box>
                                    </SimpleGrid>

                                    <Text fontSize="xs" color="gray.600">
                                        Enter the DTMF key (0-9, *, #) received from IVR webhook
                                    </Text>

                                    {/* Per-Response Timeout */}
                                    {/* <Box pt={2} borderTop="1px solid" borderColor="orange.200">
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Box>
                                                <Text fontSize="xs" fontWeight="medium">Add Time Condition</Text>
                                                <Text fontSize="10px" color="gray.600" mt={0.5}>
                                                    Check if response received within time limit
                                                </Text>
                                            </Box>
                                            <Switch
                                                isChecked={response.timeoutEnabled || false}
                                                onChange={(e) => {
                                                    const newResponses = [...(nodeConfig.dtmfResponses || [])];
                                                    newResponses[index] = {
                                                        ...response,
                                                        timeoutEnabled: e.target.checked,
                                                        timeoutDuration: response.timeoutDuration || 30,
                                                        timeoutUnit: response.timeoutUnit || 'seconds'
                                                    };
                                                    setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                                                }}
                                            />
                                        </Flex>

                                        {response.timeoutEnabled && (
                                            <Box p={2} bg="#fffbeb" border="1px solid" borderColor="orange.200" borderRadius="md">
                                                <SimpleGrid columns={2} spacing={2}>
                                                    <Box>
                                                        <FormLabel fontSize="10px">Duration</FormLabel>
                                                        <Input
                                                            bg={"white"}
                                                            type="number"
                                                            min={1}
                                                            value={response.timeoutDuration || 30}
                                                            onChange={(e) => {
                                                                const newResponses = [...(nodeConfig.dtmfResponses || [])];
                                                                newResponses[index] = {
                                                                    ...response,
                                                                    timeoutDuration: parseInt(e.target.value) || 1
                                                                };
                                                                setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                                                            }}
                                                            size="xs"
                                                        />
                                                    </Box>
                                                    <Box>
                                                        <FormLabel fontSize="10px">Unit</FormLabel>
                                                        <Select
                                                            bg={"white"}
                                                            value={response.timeoutUnit || 'seconds'}
                                                            onChange={(e) => {
                                                                const newResponses = [...(nodeConfig.dtmfResponses || [])];
                                                                newResponses[index] = {
                                                                    ...response,
                                                                    timeoutUnit: e.target.value
                                                                };
                                                                setNodeConfig({ ...nodeConfig, dtmfResponses: newResponses });
                                                            }}
                                                            size="xs"
                                                        >
                                                            <option value="seconds">Seconds</option>
                                                            <option value="minutes">Minutes</option>
                                                        </Select>
                                                    </Box>
                                                </SimpleGrid>
                                            </Box>
                                        )}
                                    </Box> */}
                                </VStack>
                            </Box>
                        ))}
                    </VStack>
                )}
            </Box>

            <Divider />

            {/* No Response Configuration */}
            <Box>
                <Flex justify="space-between" align="center" mb={3}>
                    <Box>
                        <Text fontSize="sm" fontWeight="medium">No Response / Fallback</Text>
                        <Text fontSize="xs" color="gray.600" mt={0.5}>
                            Configure wait time if no DTMF response is received
                        </Text>
                    </Box>
                    <Switch
                        isChecked={nodeConfig.noResponseWaitEnabled || false}
                        onChange={(e) => setNodeConfig({
                            ...nodeConfig,
                            noResponseWaitEnabled: e.target.checked
                        })}
                    />
                </Flex>

                {nodeConfig.noResponseWaitEnabled && (
                    <Box p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" bg="#fbf9fa">
                        <Flex align="center" gap={2} fontSize="xs" color="gray.700" mb={3}>
                            <Icon as={FaClock} />
                            <Text fontWeight="medium">Wait time for no response</Text>
                        </Flex>

                        <SimpleGrid columns={2} spacing={2} mb={3}>
                            <Box>
                                <FormLabel fontSize="xs">Wait Time</FormLabel>
                                <Input
                                    type="number"
                                    // min={1}
                                    value={nodeConfig.noResponseWaitTime }
                                    onChange={(e) => setNodeConfig({
                                        ...nodeConfig,
                                        noResponseWaitTime: parseInt(e.target.value) 
                                    })}
                                    size="sm"
                                />
                            </Box>
                            <Box>
                                <FormLabel fontSize="xs">Unit</FormLabel>
                                <Select
                                    value={nodeConfig.noResponseWaitUnit || 'seconds'}
                                    onChange={(e) => setNodeConfig({
                                        ...nodeConfig,
                                        noResponseWaitUnit: e.target.value
                                    })}
                                    size="sm"
                                >
                                    <option value="seconds">Seconds</option>
                                    <option value="minutes">Minutes</option>
                                </Select>
                            </Box>
                        </SimpleGrid>

                        <Box fontSize="xs" color="gray.700" bg="gray.100" px={2} py={1.5} borderRadius="md">
                            <Text fontWeight="semibold">Note:</Text> The "No Response" path will trigger if no DTMF input is received within {nodeConfig.noResponseWaitTime || 30} {nodeConfig.noResponseWaitUnit || 'seconds'} from the IVR call connection.
                        </Box>
                    </Box>
                )}
            </Box>

            <Button
                colorScheme="orange"
                onClick={updateNodeData}
                leftIcon={<Icon as={FaSave} />}
            >
                Update Node
            </Button>
        </VStack>
    );
};

export default DTMFNodeProperties;